import { Component, announce, defineElement } from '@theme/component';
import { deferAnimationsToFirstPaint } from '@theme/card-enter';
import { morph } from '@theme/morph';
import { RailNavigation } from '@theme/rail-navigation';

/**
 * Progressive enhancement for the blog index: category filtering and pagination without a full page load.
 */

const CARD_STAGGER_LIMIT = 8;
const OUTGOING_MS = 130;
const INCOMING_MS = 220;
const MAX_ANIMATED_CARDS = 16;
const SENTINEL_LEAD_VIEWPORTS = 3;
const NAVIGATION_CACHE_LIMIT = 12;
const NAVIGATION_CACHE_LIFETIME_MS = 300_000;

const isFeatureItem = (item) => item.classList.contains('blog__grid-item--feature');

const readColumns = (styles, property) => {
  const columns = Number.parseInt(styles.getPropertyValue(property), 10);
  return Number.isFinite(columns) && columns > 0 ? columns : 1;
};

const requestedRow = (item, property) => {
  const row = Number.parseInt(item.dataset[property] ?? '', 10);
  return Number.isFinite(row) && row > 0 ? row : 1;
};

/**
 * Resolves the grid row each feature row takes at one column count, and how many cards precede it.
 *
 * Must match snippets/blog-feature-rows.liquid.
 *
 * @param {number[]} requestedRows - The merchant's rows, in placement order.
 * @param {number} cardCount - Cards available to fill the rows in front of the feature rows.
 * @param {number} columns - Grid columns at the breakpoint being resolved.
 * @returns {{row: number, cardsBefore: number, reached: boolean}[]} One record per feature row.
 */
export function resolveFeatureRows(requestedRows, cardCount, columns) {
  const placement = [];
  let lastRow = 0;
  let remaining = cardCount;
  let cardsBefore = 0;

  for (const requested of requestedRows) {
    const wanted = Math.max(requested, lastRow + 1);
    const needed = (wanted - 1 - lastRow) * columns;

    if (needed <= remaining) {
      remaining -= needed;
      cardsBefore += needed;
      lastRow = wanted;
      placement.push({ row: lastRow, cardsBefore, reached: true });
    } else {
      const filledRows = Math.floor(remaining / columns);
      const filledCards = filledRows * columns;
      remaining -= filledCards;
      cardsBefore += filledCards;
      lastRow += filledRows + 1;
      placement.push({ row: lastRow, cardsBefore, reached: false });
    }
  }

  return placement;
}

/**
 * The first featured row that could not reach the row the merchant asked for.
 * It and everything after it are held back while more articles are still coming.
 *
 * @param {{reached: boolean}[]} placement - Records from `resolveFeatureRows`.
 * @returns {number} The index to hold from, or the length when every row landed.
 */
function heldFrom(placement) {
  const index = placement.findIndex((entry) => !entry.reached);
  return index === -1 ? placement.length : index;
}

class BlogIndex extends Component {
  ready() {
    this.animations = new Set();
    this.featureLayout = { columns: { desktop: 1, mobile: 1 }, cardsBefore: { desktop: [], mobile: [] }, heldFrom: 0 };
    this.reducedMotion = window.DieuxFrame?.motionQuery
      ?? window.matchMedia('(prefers-reduced-motion: reduce)');

    this.navigation = new RailNavigation({
      root: this,
      linkSelector: '[data-blog-nav-link], .blog__pagination a[href]',
      tabSelector: '[data-blog-nav-link]',
      historyKey: 'dieuxBlogIndex',
      rail: () => this.querySelector('[data-blog-rail]'),
      busyTarget: () => this,
      status: () => this.querySelector('[data-blog-status]'),
      sectionId: () => this.dataset.sectionId,
      activeLink: () => this.querySelector('[data-blog-nav-link][aria-current="page"]'),
      announcements: () => ({
        loading: this.dataset.blogLoadingTemplate,
        error: this.dataset.blogErrorTemplate,
        success: this.dataset.blogSuccessTemplate,
      }),
      prefetchDisabled: () => this.hasAttribute('data-blog-prefetch-disabled'),
      cacheLimit: NAVIGATION_CACHE_LIMIT,
      cacheLifetime: NAVIGATION_CACHE_LIFETIME_MS,
      parse: (parsed) => parsed.querySelector('blog-index'),
      commit: (incoming) => this.commit(incoming),
      documentTitle: (incoming) => incoming?.dataset.documentTitle,
      transition: {
        out: () => this.animate('out'),
        in: () => this.animate('in'),
      },
    });

    this.userAdvanced = false;
    this.listen(window, 'wheel', this.markUserAdvanced, { passive: true });
    this.listen(window, 'touchmove', this.markUserAdvanced, { passive: true });
    this.listen(window, 'keydown', this.markUserAdvanced, { passive: true });
    this.listen(window, 'pointerdown', this.markUserAdvanced, { passive: true });

    this.navigation.connect();
    this.navigation.warmAll();
    this.startInfiniteScroll();

    this.listen(document, 'visibilitychange', this.onVisibilityChange);
  }

  markUserAdvanced = () => {
    this.userAdvanced = true;
  };

  onVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;

    this.navigation?.warmAll();
  };

  teardown() {
    this.navigation?.disconnect();
    this.navigation = null;
    this.sentinelObserver?.disconnect();
    this.sentinelObserver = null;
    this.columnsQuery?.removeEventListener?.('change', this.onColumnsChange);
    this.columnsQuery = null;
    if (this.trailingKey) window.DieuxFrame.cancelMeasure(this.trailingKey);
    this.cancelAnimations();
  }

  /**
   * Replaces the index with the view that just loaded.
   *
   * @param {Element} incoming - The parsed `blog-index` from the response.
   * @returns {void}
   */
  commit(incoming) {
    morph(this, incoming);
    this.navigation?.warmAll();
    this.userAdvanced = false;
    this.startInfiniteScroll();
  }

  get grid() {
    return this.querySelector('[data-blog-grid]');
  }

  get cards() {
    return [...this.querySelectorAll('.blog__grid-item')];
  }

  get sentinel() {
    return this.querySelector('[data-blog-sentinel]');
  }

  /**
   * Whether the end of the list is close enough to ask for the next page.
   *
   * @returns {boolean} True while the next page is still owed.
   */
  get needsNextPage() {
    const sentinel = this.sentinel;
    if (!sentinel) return false;

    const lead = window.innerHeight * (1 + SENTINEL_LEAD_VIEWPORTS);
    return sentinel.getBoundingClientRect().top <= lead;
  }

  /**
   * Hands the pages to the scroll.
   *
   * @returns {void}
   */
  startInfiniteScroll() {
    this.sentinelObserver?.disconnect();
    this.sentinelObserver = null;
    this.loadingPage = false;

    if (!this.columnsQuery) {
      this.columnsQuery = window.matchMedia('(min-width: 46.875rem)');
      this.columnsQuery.addEventListener('change', this.onColumnsChange);
    }

    this.syncFeatureRows();

    const sentinel = this.sentinel;
    if (!sentinel) {
      this.removeAttribute('data-blog-infinite');
      this.syncTrailingRow();
      return;
    }

    this.setAttribute('data-blog-infinite', 'active');
    this.syncTrailingRow();

    this.sentinelObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
      },
      { rootMargin: `0px 0px ${SENTINEL_LEAD_VIEWPORTS * 100}% 0px` }
    );
    this.sentinelObserver.observe(sentinel);
  }

  onColumnsChange = () => this.syncTrailingRow();

  /**
   * Loads the next page and appends it to the list.
   *
   * @returns {Promise<void>} Resolves once the page has landed or been declined.
   */
  async loadNextPage() {
    const sentinel = this.sentinel;
    const nextUrl = sentinel?.dataset.blogNextUrl;
    if (this.loadingPage || !nextUrl || !this.navigation) return;

    this.loadingPage = true;
    this.grid?.setAttribute('aria-busy', 'true');

    try {
      const incoming = await this.navigation.load(nextUrl);
      if (!this.isConnected || this.sentinel !== sentinel) return;

      const grid = this.grid;
      const incomingItems = [...incoming.querySelectorAll('.blog__grid-item')].filter(
        (item) => !isFeatureItem(item)
      );
      if (!grid || !incomingItems.length) {
        this.completeInfiniteScroll();
        this.announceStatus(this.statusTemplate('blogCompleteTemplate'));
        return;
      }

      const appended = incomingItems.map((item) => document.importNode(item, true));
      grid.append(...appended);
      this.syncFeatureRows();

      const followingUrl = incoming
        .querySelector('[data-blog-sentinel]')
        ?.getAttribute('data-blog-next-url');
      const loadedMessage = this.statusTemplate(
        appended.length === 1 ? 'blogLoadedOneTemplate' : 'blogLoadedOtherTemplate'
      ).replace('__COUNT__', String(appended.length));
      if (followingUrl) {
        sentinel.dataset.blogNextUrl = followingUrl;
        this.announceStatus(loadedMessage);
      } else {
        this.completeInfiniteScroll();
        this.announceStatus(loadedMessage, this.statusTemplate('blogCompleteTemplate'));
      }

      this.syncTrailingRow();

      this.animate('in', appended);
    } catch {
      if (this.isConnected) this.abandonInfiniteScroll();
    } finally {
      this.loadingPage = false;
      this.grid?.setAttribute('aria-busy', 'false');
    }

    if (this.isConnected && this.needsNextPage) this.loadNextPage();
  }

  /**
   * The list has run out: every article is on the page.
   *
   * @returns {void}
   */
  completeInfiniteScroll() {
    this.stopObservingSentinel();
    this.setAttribute('data-blog-infinite', 'complete');
    this.syncTrailingRow();
  }

  /**
   * A page failed, so the scroll gives the pages back.
   *
   * @returns {void}
   */
  abandonInfiniteScroll() {
    this.stopObservingSentinel();
    this.removeAttribute('data-blog-infinite');
    this.syncTrailingRow();
  }

  /**
   * Reads one of the status templates the section renders on the live region.
   *
   * @param {string} key - Dataset key on `[data-blog-status]`.
   * @returns {string} The template, or an empty string.
   */
  statusTemplate(key) {
    return this.querySelector('[data-blog-status]')?.dataset[key] ?? '';
  }

  /**
   * Announces the infinite scroll's progress through the section's status region.
   *
   * @param {...string} messages - Sentences to announce together.
   * @returns {void}
   */
  announceStatus(...messages) {
    if (!this.userAdvanced) return;
    announce(this.querySelector('[data-blog-status]'), messages.filter(Boolean).join(' '));
  }

  stopObservingSentinel() {
    this.sentinelObserver?.disconnect();
    this.sentinelObserver = null;
    this.sentinel?.remove();
  }

  /**
   * Re-resolves the row every feature takes now that the grid holds more cards,
   * and moves each one to the card it now follows so the reading order matches
   * the desktop layout.
   *
   * @returns {void}
   */
  syncFeatureRows() {
    const grid = this.grid;
    if (!grid) return;

    const items = [...grid.children];
    const features = items.filter(isFeatureItem);
    const cards = items.filter((item) => !isFeatureItem(item));

    const styles = getComputedStyle(grid);
    const columns = {
      desktop: readColumns(styles, '--blog-grid-columns-desktop'),
      mobile: readColumns(styles, '--blog-grid-columns-mobile'),
    };

    const desktop = resolveFeatureRows(
      features.map((item) => requestedRow(item, 'featureRowDesktop')),
      cards.length,
      columns.desktop
    );
    const mobile = resolveFeatureRows(
      features.map((item) => requestedRow(item, 'featureRowMobile')),
      cards.length,
      columns.mobile
    );

    features.forEach((item, index) => {
      item.style.setProperty('--blog-feature-row-desktop', `${desktop[index].row}`);
      item.style.setProperty('--blog-feature-row-mobile', `${mobile[index].row}`);

      const anchor = cards[desktop[index].cardsBefore];
      if (anchor) grid.insertBefore(item, anchor);
      else grid.append(item);
    });

    this.featureLayout = {
      columns,
      cardsBefore: {
        desktop: desktop.map((entry) => entry.cardsBefore),
        mobile: mobile.map((entry) => entry.cardsBefore),
      },
      heldFrom: Math.min(heldFrom(desktop), heldFrom(mobile)),
    };
  }

  /**
   * Holds back a final row that has not been filled yet.
   *
   * @returns {void}
   */
  syncTrailingRow() {
    window.DieuxFrame.measure(this.trailingKey ??= Symbol('blog-trailing-row'), () => {
      if (!this.isConnected) return;

      const grid = this.grid;
      const holdingBack = this.getAttribute('data-blog-infinite') === 'active';
      const desktop = this.columnsQuery?.matches ?? false;
      const layout = this.featureLayout;
      const columns = desktop ? layout.columns.desktop : layout.columns.mobile;
      const cardsBefore = desktop ? layout.cardsBefore.desktop : layout.cardsBefore.mobile;

      const items = grid ? [...grid.children] : [];
      const features = items.filter(isFeatureItem);
      const cards = items.filter((item) => !isFeatureItem(item));

      const shown = holdingBack ? Math.min(layout.heldFrom, features.length) : features.length;
      const run = Math.max(0, cards.length - (shown > 0 ? cardsBefore[shown - 1] ?? 0 : 0));
      const hidden = holdingBack && columns > 1 ? run % columns : 0;

      window.DieuxFrame.write(() => {
        if (!this.isConnected) return;

        features.forEach((item, index) => {
          item.toggleAttribute('hidden', index >= shown);
        });
        cards.forEach((item, index) => {
          item.toggleAttribute('hidden', index >= cards.length - hidden);
        });
      });
    });
  }

  /**
   * Fades the visible cards out and the replacing ones in.
   *
   * @param {'in'|'out'} direction - Which half of the transition to run.
   * @param {HTMLElement[]} [cards] - Cards to animate. Defaults to the whole grid,
   *   which is what a category change replaces; an appended page passes only its
   *   own cards so the list above it stays still.
   * @returns {Promise<void>} Resolves when the animation settles.
   */
  animate(direction, cards = this.cards) {
    const visible = cards.filter((card) => {
      const bounds = card.getBoundingClientRect();
      return bounds.bottom > 0 && bounds.top < window.innerHeight;
    });
    const targets = (visible.length ? visible : cards).slice(0, MAX_ANIMATED_CARDS);

    if (!targets.length || this.reducedMotion.matches || typeof targets[0].animate !== 'function') {
      return Promise.resolve();
    }

    const entering = direction === 'in';
    const keyframes = entering
      ? [
          { opacity: 0, transform: 'translateY(16px) scale(0.988)' },
          { opacity: 1, transform: 'translateY(0) scale(1)' },
        ]
      : [
          { opacity: 1, transform: 'translateY(0) scale(1)' },
          { opacity: 0, transform: 'translateY(-12px) scale(0.99)' },
        ];
    const animations = targets.map((target, index) => {
      const animation = target.animate(keyframes, {
        duration: entering ? INCOMING_MS : OUTGOING_MS,
        delay: Math.min(index, CARD_STAGGER_LIMIT - 1) * (entering ? 20 : 8),
        easing: entering ? 'cubic-bezier(.32, .06, .22, 1)' : 'cubic-bezier(.4, 0, 1, 1)',
        fill: 'both',
      });
      this.animations.add(animation);
      return animation;
    });

    if (entering) deferAnimationsToFirstPaint(animations);

    return Promise.all(
      animations.map((animation) => animation.finished.catch(() => {}))
    ).then(() => {
      animations.forEach((animation) => {
        this.animations.delete(animation);
        animation.cancel();
      });
    });
  }

  cancelAnimations() {
    this.animations?.forEach((animation) => animation.cancel());
    this.animations?.clear();
  }
}

defineElement('blog-index', BlogIndex);
